import Honeybadger from '@honeybadger-io/js';
import jwtDecode from 'jwt-decode';

import { hydrateToken } from '/src/utils/auth';
import { updateObject, isObject } from '/src/utils/object';

const ignorePatterns = [
  /unauthorized/i, // ignore unauthorized errors. TODO: remove when deprecating v1
  /already exists/i, // these errors are handled by the components
  /no active subscription: snapshots/i,
  /no active subscription: recordings/i,
  /trouble accessing the page/i,
  /ResizeObserver/i, // ignore ResizeObserver errors, they are safe to ignore and thrown by Chrome
  /loading chunk \d+ failed/i, // ignore chunk errors, they only happen after a deploy when a user has a cached bundle
  /invalid admin credentials/i,
  /InvalidAdminCredentialsError/i,
  /UnhandledPromiseRejectionWarning/i,
  /Invalid time value/,
  /Object\.fromEntries is not a function/, // old browsers, we show a banner to update their browser
  /Shared resource doesn't exists/, // https://app.shortcut.com/crazyegg/story/34163/honeybadger-investigation-for-shell#activity-34171
  /Share URL is not valid/,
  /(?<!Site\s)not\sfound/i,
];

const botUserAgentPatterns = [
  /googlebot/i,
  /adsbot-google/i,
  /google-inspectiontool/i,
  /bingbot/i,
  /duckduckbot/i,
  /yandexbot/i,
  /baiduspider/i,
  /applebot/i,
  /semrushbot/i,
];

export const sensitiveKeys = [
  'password',
  'creditcard',
  'cardNumber',
  'card_number',
  'ccNumber',
  'ccSecurityCode',
  'card_security_code',
  'cardSecurityCode',
];

const MAX_SANITIZE_DEPTH = 7;
const MAX_SANITIZE_ARRAY_ITEMS = 60;
const MAX_SANITIZE_OBJECT_KEYS = 60;

let sanitizerHooksInstalled = false;

export function isBotUserAgent() {
  const userAgent = window.navigator?.userAgent || '';
  return botUserAgentPatterns.some((pattern) => pattern.test(userAgent));
}

function domTypeName(value) {
  return value?.constructor?.name || 'DOMObject';
}

function isDomLikeObject(value) {
  if (!value || typeof value !== 'object') return false;

  if (typeof Window !== 'undefined' && value instanceof Window) return true;
  if (typeof Document !== 'undefined' && value instanceof Document) return true;
  if (typeof Element !== 'undefined' && value instanceof Element) return true;
  if (typeof Node !== 'undefined' && value instanceof Node) return true;

  return ['Window', 'Document', 'HTMLDocument', 'Element', 'Node'].includes(domTypeName(value));
}

function elementLabel(value) {
  if (!value || typeof value !== 'object') return null;

  const tagName = typeof value.tagName === 'string' ? value.tagName.toLowerCase() : value.nodeName?.toLowerCase?.();
  const elementId = typeof value.id === 'string' && value.id ? `#${value.id}` : '';

  let classLabel = '';
  if (typeof value.className === 'string' && value.className.trim()) {
    classLabel = `.${value.className.trim().split(/\s+/).slice(0, 3).join('.')}`;
  }

  return `${tagName || 'node'}${elementId}${classLabel}`;
}

function sanitizeEvent(value) {
  return {
    type: value.type,
    is_trusted: value.isTrusted,
    time_stamp: value.timeStamp,
    target: elementLabel(value.target),
    current_target: elementLabel(value.currentTarget),
  };
}

function sanitizeForHoneybadger(value, seen = new WeakSet(), depth = 0) {
  if (value === null || value === undefined) return value;

  const valueType = typeof value;
  if (valueType === 'string' || valueType === 'number' || valueType === 'boolean') return value;
  if (valueType === 'bigint') return value.toString();
  if (valueType === 'symbol') return value.toString();
  if (valueType === 'function') return '[Function]';

  if (value instanceof Date) {
    const timestamp = value.getTime();
    if (Number.isFinite(timestamp)) return value.toISOString();
    return value.toString() || '[Invalid Date]';
  }
  if (value instanceof RegExp) return value.toString();
  if (typeof URL !== 'undefined' && value instanceof URL) return value.toString();

  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
    };
  }

  if (typeof Event !== 'undefined' && value instanceof Event) {
    return sanitizeEvent(value);
  }

  if (isDomLikeObject(value)) {
    return `[${domTypeName(value)}]`;
  }

  if (depth >= MAX_SANITIZE_DEPTH) return '[DepthLimit]';
  if (seen.has(value)) return '[Circular]';
  seen.add(value);

  if (Array.isArray(value)) {
    const sanitizedArray = value
      .slice(0, MAX_SANITIZE_ARRAY_ITEMS)
      .map((item) => sanitizeForHoneybadger(item, seen, depth + 1));

    if (value.length > MAX_SANITIZE_ARRAY_ITEMS) {
      sanitizedArray.push(`[Truncated:${value.length - MAX_SANITIZE_ARRAY_ITEMS}]`);
    }

    return sanitizedArray;
  }

  if (value instanceof Map) {
    const mapped = {};
    for (const [key, mapValue] of Array.from(value.entries()).slice(0, MAX_SANITIZE_OBJECT_KEYS)) {
      mapped[String(key)] = sanitizeForHoneybadger(mapValue, seen, depth + 1);
    }
    return mapped;
  }

  if (value instanceof Set) {
    return Array.from(value.values())
      .slice(0, MAX_SANITIZE_ARRAY_ITEMS)
      .map((item) => sanitizeForHoneybadger(item, seen, depth + 1));
  }

  if (!isObject(value)) {
    return String(value);
  }

  const sanitizedObject = {};
  for (const key of Object.keys(value).slice(0, MAX_SANITIZE_OBJECT_KEYS)) {
    try {
      sanitizedObject[key] = sanitizeForHoneybadger(value[key], seen, depth + 1);
    } catch {
      sanitizedObject[key] = '[Unreadable]';
    }
  }

  return sanitizedObject;
}

function installSanitizerHooks() {
  if (sanitizerHooksInstalled) return;
  sanitizerHooksInstalled = true;

  const originalAddBreadcrumb = Honeybadger.addBreadcrumb.bind(Honeybadger);
  Honeybadger.addBreadcrumb = function (message, options = {}) {
    if (!isObject(options)) {
      return originalAddBreadcrumb(message, options);
    }

    return originalAddBreadcrumb(message, {
      ...options,
      metadata: sanitizeForHoneybadger(options.metadata),
    });
  };

  const originalSetContext = Honeybadger.setContext.bind(Honeybadger);
  Honeybadger.setContext = function (context) {
    return originalSetContext(sanitizeForHoneybadger(context));
  };
}

export function configure() {
  const config = {
    apiKey: window.HONEYBADGER_KEY,
    environment: window.location.hostname.match(/-preprod\.envs/) ? 'preprod' : window.ENVIRONMENT,
    revision: window.version,
    reportData: import.meta.env.PROD,
    enableUncaught: false,
    filters: sensitiveKeys,
  };

  const instance = Honeybadger.configure(config);
  installSanitizerHooks();

  Honeybadger.beforeNotify(function (notice) {
    if (isBotUserAgent()) {
      return false;
    }

    if (ignorePatterns.some((p) => p.test(notice.message))) {
      return false;
    }
    notice.context = sanitizeForHoneybadger(notice.context);
    notice.params = sanitizeForHoneybadger(notice.params);
    notice.session = sanitizeForHoneybadger(notice.session);

    filterSensitiveData(notice.context);
    filterSensitiveData(notice.params);
    filterSensitiveData(notice.session);
  });
  const userToken = hydrateToken();
  if (userToken) {
    identifyUserByToken(userToken);
  }
  return instance;
}

export function filterSensitiveData(obj) {
  if (isObject(obj)) {
    updateObject(obj, sensitiveKeys, '[FILTERED]');
  }
}

export function identifyUser({ userId, guestId, email, currentAccount = null }) {
  Honeybadger.setContext({
    session_id: window.ceSessionId,
    user_id: userId,
    guest_id: guestId,
    user_email: email,
    current_account: currentAccount,
  });
}

function identifyUserByToken(token) {
  try {
    const payload = jwtDecode(token);
    identifyUser({ userId: payload.sub, guestId: payload.guest_id, email: payload.email, currentAccount: null });
  } catch {
    console.error('Failed to parse auth token');
  }
}

export function clearContext() {
  Honeybadger.clear();
}
